/**
 * i18n source for the AI assistant's canned demo responses (BUG-29).
 *
 * UnifiedChatRouter is a plain service class (no React hooks), so it cannot
 * call useTranslation/useGlobalTranslation. This module is the i18n layer for
 * chat demo copy: it reads the CURRENT global language from the same
 * persistence SettingsContext writes (`localStorage['app-settings'].language`,
 * updated synchronously by updateSettings), so every NEW assistant message
 * follows the live language — EN→ES→EN round-trips included.
 *
 * Demo copy ships in English and Spanish (the two demo locales). Other
 * languages fall back to English.
 */

import { getDemoRegion } from '@/lib/demo/demo-region';

export type ChatDemoLanguage = 'en' | 'es';

export function getChatLanguage(): ChatDemoLanguage {
  try {
    const raw = localStorage.getItem('app-settings');
    if (raw) {
      const lang = JSON.parse(raw).language;
      if (lang === 'es') return 'es';
      if (lang) return 'en';
    }
  } catch {
    // fall through
  }
  return getDemoRegion() === 'mx' ? 'es' : 'en';
}

/**
 * BUG-113 — the aging fields below are named for the band they hold, and every
 * rendered label states its range explicitly. `agingNinety` is 61-90;
 * `agingNinetyPlus` is 91 and over. There used to be no 61-90 field at all, so
 * a 65-day invoice was rendered under "Antigüedad 90+ días".
 */
export interface CashFlowCopyData {
  workingCapital: string;
  receivablesTotal: string;
  payablesTotal: string;
  netPosition: string;
  overdue: string;
  agingThirty: string;
  agingSixty: string;
  agingNinety: string;
  agingNinetyPlus: string;
  topInvoices: Array<{ buyerName: string; amount: string; daysOverdue: number }>;
  dueThisWeek: string;
  dueSoon: string;
}

/**
 * BUG-113 — same fix on the receivables answer, where EVERY label was off by
 * one band: `current` (not yet due) was printed as "0-30 days", `thirty` (1-30)
 * as "30-60 days", `sixty` (31-60) as "60-90 days" and `ninety` (>60) as
 * "90+ days". `ninety` now means 61-90 and `ninetyPlus` is new.
 */
export interface ReceivablesCopyData {
  totalOutstanding: string;
  current: string;
  currentPct: number;
  thirty: string;
  thirtyPct: number;
  sixty: string;
  sixtyPct: number;
  ninety: string;
  ninetyPct: number;
  ninetyPlus: string;
  ninetyPlusPct: number;
  priorityList: string;
  overdueCount: number;
  overdueAmount: string;
}

/**
 * BUG-101 — the payables answer, driven by seeded data.
 *
 * Deliberately NOT modelled on the old `payablesInsight` block, which was a
 * hardcoded string carrying invented suppliers ("SupplyCo Industries",
 * "TechVendor LLC", "GlobalParts Inc") and invented figures ($634,890 total,
 * $4,840/month savings, "found 2 issues worth $3,200"). None of that is
 * derivable from a tenant's data, and on the MX demo it would contradict the
 * dashboard outright.
 *
 * Every field here comes from the seeded bills. There is no discount-terms or
 * early-payment-savings field, because nothing in the dataset supports one.
 */
export interface PayablesCopyData {
  total: string;
  dueThisWeek: string;
  dueSoon: string;
  billList: string;
  billCount: number;
}

const en = {
  // ── Tell Tale / email ────────────────────────────────────────────────
  emailIntro: 'Tell Tale is processing your email-related query.',
  emailFound: (count: number) =>
    `I found ${count} recent emails to process. The latest one appears to be about a payment delay.`,
  emailNone: "I don't see any unprocessed emails at the moment.",
  // BUG-114: these two were hardcoded English literals in UnifiedChatRouter,
  // rendered as-is inside a Spanish session.
  emailActionProcess: 'Process Recent Emails',
  emailActionViewInbox: 'View Full Inbox',
  // ── System ───────────────────────────────────────────────────────────
  unknownQuery: (query: string) =>
    `I'm not sure how to handle "${query}". Could you try rephrasing it or let me know if you need help with emails, financial operations, or general support?`,
  errorResponse: 'I encountered an issue processing your request. Please try again.',
  // ── Windrose financial insight ───────────────────────────────────────
  // ── Poniente cash flow (data-driven) ─────────────────────────────────
  cashFlow: (d: CashFlowCopyData) => `**Poniente Cash Optimization analyzing your query...**

Your current cash position is ${d.workingCapital}. Here is the full cash flow behind that number, from your actual seeded data:

💰 **Cash Flow Snapshot (from database):**
- Working Capital: ${d.workingCapital}
- Total Receivables: ${d.receivablesTotal}
- Total Payables: ${d.payablesTotal}
- Net Position: ${d.netPosition}

🔄 **Receivables Analysis (from seeded data):**
- Total Overdue: ${d.overdue}
- Aging 1-30 days: ${d.agingThirty}
- Aging 31-60 days: ${d.agingSixty}
- Aging 61-90 days: ${d.agingNinety}
- Aging 90+ days: ${d.agingNinetyPlus}

📋 **Top Overdue Invoices:**
${d.topInvoices.map((inv, idx) => `${idx + 1}. ${inv.buyerName}: ${inv.amount} (${inv.daysOverdue} days overdue)`).join('\n')}

💸 **Payables Due:**
- Due This Week: ${d.dueThisWeek}
- Due Next Week: ${d.dueSoon}

⚡ **Smart Actions (based on your actual data):**
- Prioritize collecting ${d.overdue} in overdue receivables
- Manage ${d.dueThisWeek} in upcoming payables
- Improve net position of ${d.netPosition}`,
  // NOTE (BUG-104): `financialInsight` / `financialActions` lived here. The
  // prose opened "Based on your demo account data" and then quoted $487,234 —
  // MOCK_DEMO_DATA_BASE's cash position, not the tenant's — plus an invented
  // DSO, customer-concentration ratio and $89K of "collections". Its only
  // caller was `generateFinancialInsight`, removed in the same change. Left in
  // place it is a landmine: the next person wires it up in good faith and
  // ships fabricated figures again.
  // NOTE (BUG-105): `riskInsight` / `riskActions` / `complianceInsight` /
  // `complianceActions` lived here. Both templates opened by naming an agent as
  // actively working — "Levante Credit Risk Engine analyzing...", "Hooked In
  // Compliance Monitor analyzing..." — over invented figures (6.2 portfolio
  // score, 98% compliance rate, $42,300 saved YTD, 150 checks run). Their only
  // callers were removed in the same change. Deleted rather than left dormant:
  // an orphaned fabricated template is a landmine the next person wires up in
  // good faith, which is exactly how PRD-38 lost the cash-flow answer for seven
  // weeks.
  cashFlowActions: ['Auto-Optimize Payment Schedule →', 'Setup Early Payment Program', 'Run What-If Scenarios'],
  cashFlowError: 'Unable to fetch financial data',
  // ── Levante risk ─────────────────────────────────────────────────────
  // ── Receivables (data-driven) ────────────────────────────────────────
  receivablesError:
    "**Windrose + Poniente**: Unable to fetch receivables data. Please ensure you're logged in and have access to financial data.",
  receivablesNoOverdue: 'No overdue invoices at this time. Great job! 🎉',
  receivablesInvoiceAction: (daysOverdue: number) =>
    daysOverdue > 60 ? 'Escalate to collections' : 'Send payment reminder + offer payment plan',
  receivablesPriority: (daysOverdue: number) => (daysOverdue > 60 ? 'HIGH' : 'MEDIUM'),
  receivablesInvoiceLine: (inv: { buyer: string; amount: string; daysOverdue: number; invoiceNumber: string; action: string; priority: string }) =>
    `**${inv.buyer}** - ${inv.amount} (${inv.daysOverdue} days overdue)
   - Invoice: ${inv.invoiceNumber}
   - Action: ${inv.action}
   - Priority: ${inv.priority}`,
  receivables: (d: ReceivablesCopyData) => `**Windrose + Poniente analyzing receivables...**

📬 **Receivables Intelligence:**

**Current AR Status:**
- Total Outstanding: ${d.totalOutstanding}
- Current (not yet due): ${d.current} (${d.currentPct}%)
- 1-30 days: ${d.thirty} (${d.thirtyPct}%)
- 31-60 days: ${d.sixty} (${d.sixtyPct}%)
- 61-90 days: ${d.ninety} (${d.ninetyPct}%)
- 90+ days: ${d.ninetyPlus} (${d.ninetyPlusPct}%) ${d.ninetyPlusPct > 10 ? '⚠️' : ''}

🎯 **Collection Priority:**

${d.priorityList}

💡 **AI-Powered Actions:**
- Auto-draft collection emails (personalized for each customer)
- Smart payment plan calculator (maximizes recovery vs. relationship)
- Predict which invoices need immediate attention (83% accuracy)
${d.overdueCount > 0 ? `\n**Potential Recovery**: ${d.overdueAmount} from ${d.overdueCount} overdue invoices` : ''}`,
  receivablesActions: ['Start Collection Workflow →', 'Generate Payment Plans', 'Analyze Customer Risk'],
  // ── Payables ─────────────────────────────────────────────────────────
  payablesInsight: `**Poniente Payment Optimizer analyzing...**

💸 **Payables Optimization:**

**Current AP Status:**
- Total Payables: $634,890
- Due in 7 days: $89,400
- Due in 30 days: $234,560
- Early Payment Eligible: $156,780

⚡ **Smart Payment Schedule:**

**This Week:**
1. SupplyCo Industries - $42,100 (2/10 terms available — pay early, save $842)
2. TechVendor LLC - $18,900 (no discount, pay on due date to preserve working capital)

**Next Week:**
3. GlobalParts Inc - $67,400 (3/15 terms — recommended early payment saves $2,022)

💡 **Cash Flow Impact:**
- Optimal payment timing saves $4,840/month
- Current strategy: Paying too early (losing $2,100 in float)
- Recommendation: Use Poniente auto-scheduler

🎯 **AI Actions:**
- Auto-schedule payments to maximize discounts + float
- Predict which vendors likely to negotiate better terms
- Identify duplicate payments and overbilling (found 2 issues worth $3,200)`,
  payablesActions: ['Enable Auto-Payment Scheduling →', 'Review Discount Opportunities', 'AI-Negotiate Better Terms'],
  // ── Payables (data-driven, BUG-101) ──────────────────────────────────
  payablesNoBills: 'No outstanding bills recorded for this period.',
  payablesBillLine: (b: { label: string; amount: string; dueDate: string; status: string }) =>
    `**${b.label}** — ${b.amount}
   - Due: ${b.dueDate}
   - Status: ${b.status}`,
  payables: (d: PayablesCopyData) => `**Poniente analyzing payables...**

💸 **Payables Position:**

**Current AP Status:**
- Total Outstanding: ${d.total}
- Due this week: ${d.dueThisWeek}
- Due in the next two weeks: ${d.dueSoon}

📋 **Bills to Pay:**

${d.billList}

💡 **AI-Powered Actions:**
- Stagger payments against the cash-flow forecast
- Flag bills approaching their due date before they age
- Surface duplicate or already-paid bills before they are paid twice
${d.billCount > 0 ? `\n**Bills tracked**: ${d.billCount}` : ''}`,
  // ── Compliance ───────────────────────────────────────────────────────
  // ── Help (Zephyr) ────────────────────────────────────────────────────
  pageHelp: {
    dashboard: 'View your financial overview, cash position, and key metrics',
    receivables: 'Manage customer invoices, track aging, and accelerate collections',
    payables: 'Optimize vendor payments, capture discounts, and manage cash flow',
    analytics: 'Deep dive into financial analytics, trends, and forecasts',
    messages: 'AI-powered email processing and intelligent routing',
    fallback: 'Navigate through your financial operations',
  } as Record<string, string>,
  help: (currentPage: string, pageHelpText: string) => `**Zephyr Support Assistant here!**

I can help you understand Alisio Fi's AI-native capabilities:

🤖 **AI Agents Available:**
- **Windrose**: Financial analytics & strategic insights
- **Poniente**: Cash flow optimization & payment timing
- **Levante**: Credit risk analysis & customer monitoring
- **Tell Tale**: Email processing & document intelligence
- **Hooked In**: Compliance monitoring & regulatory alerts
- **Full Send**: Cross-border optimization & growth
- **Line Type**: Labor analytics & workforce insights
- **Zephyr**: That's me! Your support guide

📍 **Current Page**: ${currentPage}
${pageHelpText}

💡 **Try asking me:**
- "Analyze my cash flow"
- "Which invoices should I collect first?"
- "Are there any compliance risks?"
- "How can I optimize my payment schedule?"`,
  helpActions: ['Explore Dashboard Features →', 'Try Example Queries', 'Watch Tutorial Video'],
  // ── Multi-agent ──────────────────────────────────────────────────────
  multiAgent: (query: string) => `**Multi-Agent Coordination Engaged**

I've assembled a team of agents to handle your complex query: "${query}"

🤖 **Agents Collaborating:**
- **Windrose**: Analyzing financial patterns
- **Poniente**: Calculating cash flow impact
- **Levante**: Assessing risk factors

📊 **Coordinated Analysis:**
1. **Financial Impact** (Windrose): Your current position suggests opportunities for optimization
2. **Cash Flow** (Poniente): Timing recommendations could improve liquidity by 8-12%
3. **Risk Assessment** (Levante): Identified moderate risk factors requiring attention

💡 **Recommended Actions:**
The agents suggest starting with your receivables - there's $89K in low-effort collections that could significantly impact your cash position.`,
  multiAgentActions: ['Run Cash Flow Analysis →', 'Review Receivables', 'Check Risk Exposure'],
};

const es: typeof en = {
  emailIntro: 'Tell Tale está procesando tu consulta de correos.',
  emailFound: (count: number) =>
    `Encontré ${count} correos recientes por procesar. El más reciente parece tratarse de un retraso de pago.`,
  emailNone: 'No veo correos sin procesar en este momento.',
  emailActionProcess: 'Procesar correos recientes',
  emailActionViewInbox: 'Ver bandeja completa',
  unknownQuery: (query: string) =>
    `No estoy seguro de cómo manejar "${query}". ¿Podrías reformularlo o decirme si necesitas ayuda con correos, operaciones financieras o soporte general?`,
  errorResponse: 'Encontré un problema al procesar tu solicitud. Por favor intenta de nuevo.',
  cashFlow: (d: CashFlowCopyData) => `**Optimización de Efectivo Poniente analizando tu consulta...**

Tu posición de efectivo actual es ${d.workingCapital}. Este es el flujo de caja completo detrás de esa cifra, con tus datos reales:

💰 **Resumen de Flujo de Caja (de la base de datos):**
- Capital de Trabajo: ${d.workingCapital}
- Cuentas por Cobrar Totales: ${d.receivablesTotal}
- Cuentas por Pagar Totales: ${d.payablesTotal}
- Posición Neta: ${d.netPosition}

🔄 **Análisis de Cuentas por Cobrar (datos reales):**
- Total Vencido: ${d.overdue}
- Antigüedad 1-30 días: ${d.agingThirty}
- Antigüedad 31-60 días: ${d.agingSixty}
- Antigüedad 61-90 días: ${d.agingNinety}
- Antigüedad 90+ días: ${d.agingNinetyPlus}

📋 **Principales Facturas Vencidas:**
${d.topInvoices.map((inv, idx) => `${idx + 1}. ${inv.buyerName}: ${inv.amount} (${inv.daysOverdue} días de vencimiento)`).join('\n')}

💸 **Pagos Próximos:**
- Vencen Esta Semana: ${d.dueThisWeek}
- Vencen Próxima Semana: ${d.dueSoon}

⚡ **Acciones Inteligentes (con base en tus datos reales):**
- Priorizar el cobro de ${d.overdue} en cuentas vencidas
- Gestionar ${d.dueThisWeek} en pagos próximos
- Mejorar la posición neta de ${d.netPosition}`,
  // NOTE (BUG-105): `riskInsight` / `riskActions` / `complianceInsight` /
  // `complianceActions` lived here. Both templates opened by naming an agent as
  // actively working — "Levante Credit Risk Engine analyzing...", "Hooked In
  // Compliance Monitor analyzing..." — over invented figures (6.2 portfolio
  // score, 98% compliance rate, $42,300 saved YTD, 150 checks run). Their only
  // callers were removed in the same change. Deleted rather than left dormant:
  // an orphaned fabricated template is a landmine the next person wires up in
  // good faith, which is exactly how PRD-38 lost the cash-flow answer for seven
  // weeks.
  cashFlowActions: ['Auto-Optimizar Calendario de Pagos →', 'Configurar Programa de Pronto Pago', 'Ejecutar Escenarios What-If'],
  cashFlowError: 'No fue posible obtener los datos financieros',
  receivablesError:
    '**Windrose + Poniente**: No fue posible obtener los datos de cuentas por cobrar. Verifica que hayas iniciado sesión y tengas acceso a datos financieros.',
  receivablesNoOverdue: 'No hay facturas vencidas en este momento. ¡Buen trabajo! 🎉',
  receivablesInvoiceAction: (daysOverdue: number) =>
    daysOverdue > 60 ? 'Escalar a cobranza' : 'Enviar recordatorio de pago + ofrecer plan de pagos',
  receivablesPriority: (daysOverdue: number) => (daysOverdue > 60 ? 'ALTA' : 'MEDIA'),
  receivablesInvoiceLine: (inv) =>
    `**${inv.buyer}** - ${inv.amount} (${inv.daysOverdue} días de vencimiento)
   - Factura: ${inv.invoiceNumber}
   - Acción: ${inv.action}
   - Prioridad: ${inv.priority}`,
  receivables: (d: ReceivablesCopyData) => `**Windrose + Poniente analizando cuentas por cobrar...**

📬 **Inteligencia de Cuentas por Cobrar:**

**Estado Actual de CxC:**
- Total Pendiente: ${d.totalOutstanding}
- Corriente (aún no vence): ${d.current} (${d.currentPct}%)
- 1-30 días: ${d.thirty} (${d.thirtyPct}%)
- 31-60 días: ${d.sixty} (${d.sixtyPct}%)
- 61-90 días: ${d.ninety} (${d.ninetyPct}%)
- 90+ días: ${d.ninetyPlus} (${d.ninetyPlusPct}%) ${d.ninetyPlusPct > 10 ? '⚠️' : ''}

🎯 **Prioridad de Cobranza:**

${d.priorityList}

💡 **Acciones con IA:**
- Redacción automática de correos de cobranza (personalizados por cliente)
- Calculadora inteligente de planes de pago (maximiza recuperación vs. relación)
- Predicción de facturas que requieren atención inmediata (83% de precisión)
${d.overdueCount > 0 ? `\n**Recuperación Potencial**: ${d.overdueAmount} de ${d.overdueCount} facturas vencidas` : ''}`,
  receivablesActions: ['Iniciar Flujo de Cobranza →', 'Generar Planes de Pago', 'Analizar Riesgo de Clientes'],
  payablesInsight: `**Optimizador de Pagos Poniente analizando...**

💸 **Optimización de Cuentas por Pagar:**

**Estado Actual de CxP:**
- Total por Pagar: $634,890
- Vencen en 7 días: $89,400
- Vencen en 30 días: $234,560
- Elegible para Pronto Pago: $156,780

⚡ **Calendario Inteligente de Pagos:**

**Esta Semana:**
1. SupplyCo Industries - $42,100 (términos 2/10 disponibles — paga antes y ahorra $842)
2. TechVendor LLC - $18,900 (sin descuento, paga en la fecha de vencimiento para preservar capital de trabajo)

**Próxima Semana:**
3. GlobalParts Inc - $67,400 (términos 3/15 — el pago anticipado recomendado ahorra $2,022)

💡 **Impacto en Flujo de Caja:**
- El momento óptimo de pago ahorra $4,840/mes
- Estrategia actual: Pagando demasiado pronto (perdiendo $2,100 en flotación)
- Recomendación: Usar el auto-programador de Poniente

🎯 **Acciones de IA:**
- Auto-programar pagos para maximizar descuentos + flotación
- Predecir qué proveedores podrían negociar mejores términos
- Identificar pagos duplicados y sobrefacturación (2 casos por $3,200)`,
  payablesActions: ['Activar Programación Automática de Pagos →', 'Revisar Oportunidades de Descuento', 'Negociar Mejores Términos con IA'],
  payablesNoBills: 'No hay cuentas por pagar registradas para este periodo.',
  payablesBillLine: (b: { label: string; amount: string; dueDate: string; status: string }) =>
    `**${b.label}** — ${b.amount}
   - Vence: ${b.dueDate}
   - Estado: ${b.status}`,
  payables: (d: PayablesCopyData) => `**Poniente analizando cuentas por pagar...**

💸 **Posición de Cuentas por Pagar:**

**Estado Actual de CxP:**
- Total Pendiente: ${d.total}
- Vence esta semana: ${d.dueThisWeek}
- Vence en las próximas dos semanas: ${d.dueSoon}

📋 **Facturas por Pagar:**

${d.billList}

💡 **Acciones con IA:**
- Escalonar los pagos según el pronóstico de flujo de efectivo
- Avisar de las facturas próximas a vencer antes de que caigan en mora
- Detectar facturas duplicadas o ya pagadas antes de pagarlas dos veces
${d.billCount > 0 ? `\n**Facturas registradas**: ${d.billCount}` : ''}`,
  pageHelp: {
    dashboard: 'Consulta tu resumen financiero, posición de efectivo y métricas clave',
    receivables: 'Gestiona facturas de clientes, da seguimiento a la antigüedad y acelera la cobranza',
    payables: 'Optimiza pagos a proveedores, captura descuentos y gestiona el flujo de caja',
    analytics: 'Profundiza en analítica financiera, tendencias y pronósticos',
    messages: 'Procesamiento de correos con IA y enrutamiento inteligente',
    fallback: 'Navega por tus operaciones financieras',
  } as Record<string, string>,
  help: (currentPage: string, pageHelpText: string) => `**¡Asistente de Soporte Zephyr aquí!**

Puedo ayudarte a entender las capacidades de IA de Alisio Fi:

🤖 **Agentes de IA Disponibles:**
- **Windrose**: Analítica financiera y hallazgos estratégicos
- **Poniente**: Optimización de flujo de caja y momento de pagos
- **Levante**: Análisis de riesgo crediticio y monitoreo de clientes
- **Tell Tale**: Procesamiento de correos e inteligencia documental
- **Hooked In**: Monitoreo de cumplimiento y alertas regulatorias
- **Full Send**: Optimización transfronteriza y crecimiento
- **Line Type**: Analítica laboral y de fuerza de trabajo
- **Zephyr**: ¡Soy yo! Tu guía de soporte

📍 **Página Actual**: ${currentPage}
${pageHelpText}

💡 **Prueba preguntarme:**
- "Analiza mi flujo de caja"
- "¿Qué facturas debo cobrar primero?"
- "¿Hay riesgos de cumplimiento?"
- "¿Cómo optimizo mi calendario de pagos?"`,
  helpActions: ['Explorar Funciones del Panel →', 'Probar Consultas de Ejemplo', 'Ver Video Tutorial'],
  multiAgent: (query: string) => `**Coordinación Multi-Agente Activada**

Reuní un equipo de agentes para atender tu consulta compleja: "${query}"

🤖 **Agentes Colaborando:**
- **Windrose**: Analizando patrones financieros
- **Poniente**: Calculando impacto en flujo de caja
- **Levante**: Evaluando factores de riesgo

📊 **Análisis Coordinado:**
1. **Impacto Financiero** (Windrose): Tu posición actual sugiere oportunidades de optimización
2. **Flujo de Caja** (Poniente): Las recomendaciones de tiempo podrían mejorar la liquidez 8-12%
3. **Evaluación de Riesgo** (Levante): Se identificaron factores de riesgo moderados que requieren atención

💡 **Acciones Recomendadas:**
Los agentes sugieren comenzar con tus cuentas por cobrar: hay $89K en cobros de bajo esfuerzo que podrían impactar significativamente tu posición de efectivo.`,
  multiAgentActions: ['Ejecutar Análisis de Flujo de Caja →', 'Revisar Cuentas por Cobrar', 'Verificar Exposición al Riesgo'],
};

/** Active-language demo copy. Reads the live global language on every call. */
/**
 * Copy for the active chat language. Pass `lang` explicitly when the caller
 * already resolved it (the demo-intent path does), so the copy can never
 * disagree with the language the answer was computed for.
 */
export function chatDemoCopy(lang: ChatDemoLanguage = getChatLanguage()): typeof en {
  return lang === 'es' ? es : en;
}
